// src/pages/BookNow.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import contactBg from '../assets/BookNowbg2.png';
import formbg from '../assets/leaves.png';

// API imports – adjust paths according to your project structure
import SummaryApi from '../common/index'; // or '../common' — wherever SummaryApi is exported
import { apiCall } from '../utils/apiService';

export default function BookNow() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    patient_name: '',
    mobile_number: '',
    email: '',
    whatsapp_number: '',
    age: '',
    gender: '',
    appointment_date: '',
    appointment_time: '',
    problem_description: '',
    notify_before: 30,
  });

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // New states for success popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth()); // 0–11
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  today.setHours(0, 0, 0, 0);

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const startOffset = (firstDay + 6) % 7;

  const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  // First day of real current month
  const minDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);

  // First day of viewed month
  const currentViewDate = new Date(currentYear, currentMonth, 1);

  const isPrevDisabled = currentViewDate <= minDate;

  const changeMonth = (direction) => {
    const newDate = new Date(currentYear, currentMonth + direction, 1);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
    setSelectedDate(null);
    setSelectedTime('');
  };

  const timeSlots = [];
  for (let hour = 9; hour <= 20; hour++) {
    const ampm = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour % 12 || 12;
    for (let min = 0; min < 60; min += 30) {
      const minStr = min.toString().padStart(2, '0');
      timeSlots.push(`${displayHour}:${minStr} ${ampm}`);
    }
  }

  const isPastDate = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  // NEW: Check if a time slot is in the past (only when selected date is today)
  const isPastTimeSlot = (timeStr) => {
    if (!selectedDate) return false;

    const selectedFullDate = new Date(currentYear, currentMonth, selectedDate);
    selectedFullDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only apply past time check for TODAY
    if (selectedFullDate.getTime() !== today.getTime()) {
      return false;
    }

    // Parse the displayed time string (e.g. "9:30 AM")
    const [timePart, period] = timeStr.split(' ');
    let [hourStr, minStr] = timePart.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minStr || '0', 10);

    if (period?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
    if (period?.toUpperCase() === 'AM' && hour === 12) hour = 0;

    // Create a Date object for this time slot (today)
    const slotTime = new Date();
    slotTime.setHours(hour, minute, 0, 0);

    // Compare with current real time
    return slotTime < new Date();
  };

  // NEW: Auto-clear selected time if it becomes invalid after date change
  useEffect(() => {
    if (selectedDate && selectedTime && isPastTimeSlot(selectedTime)) {
      setSelectedTime('');
    }
  }, [selectedDate, selectedTime]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1440 : 0) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate || !selectedTime) {
      setSubmitError('Please select both date and time');
      return;
    }

    if (
      !formData.patient_name.trim() ||
      !formData.mobile_number.trim() ||
      !formData.age ||
      !formData.gender
    ) {
      setSubmitError('Please fill all required fields: Name, Mobile, Age, Gender');
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);

    try {
      const appointment_date = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}`;

      let appointment_time = '00:00:00';
      if (selectedTime) {
        const [timePart, period] = selectedTime.split(' ');
        let [hourStr, minStr] = timePart.split(':');
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minStr || '00', 10);

        if (period?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
        if (period?.toUpperCase() === 'AM' && hour === 12) hour = 0;

        appointment_time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
      }

      const payload = {
        ...formData,
        patient_name: formData.patient_name.trim(),
        mobile_number: formData.mobile_number.trim(),
        email: formData.email?.trim() || null,
        whatsapp_number: formData.whatsapp_number?.trim() || null,
        age: formData.age ? Number(formData.age) : null,
        gender: formData.gender,
        appointment_date,
        appointment_time,
        problem_description: formData.problem_description?.trim() || null,
        notify_before: Number(formData.notify_before || 0),
      };

      console.log('Sending payload to:', SummaryApi.createAppointment.url);
      console.log('Payload:', payload);

      const response = await apiCall(SummaryApi.createAppointment, payload);

      console.log('Raw response from server:', response);

      if (response?.status === true) {
        const message = `Thank You! `;
        setSuccessMessage(message);
        setShowSuccessPopup(true);

        // Reset form
        setFormData({
          patient_name: '',
          mobile_number: '',
          email: '',
          whatsapp_number: '',
          age: '',
          gender: '',
          appointment_date: '',
          appointment_time: '',
          problem_description: '',
          notify_before: 30,
        });
        setSelectedDate(null);
        setSelectedTime('');
      } else {
        throw new Error(response?.message || 'No success status');
      }
    } catch (err) {
      console.error('Create appointment failed:', err);
      setSubmitError(err.message || 'Error creating appointment. Please try again.');
    } finally {
      setSubmitLoading(false);
    }

    if (!/^\d{10}$/.test(formData.mobile_number)) {
      setSubmitError('Mobile number must be exactly 10 digits');
      return;
    }
  };

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
    setSuccessMessage('');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white font-poppins relative">
      <div className="fixed top-0 right-0 h-full w-[1px] bg-white z-50 sm:hidden"></div>
      <Navbar />

      {/* Hero Banner */}
      <section
        className="relative bg-cover bg-center py-24 md:py-32 lg:py-8 text-white flex items-center justify-center overflow-hidden font-playfair"
        style={{ backgroundImage: `url(${contactBg})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 style={{ fontSize: '4rem', lineHeight: '1.1' }} className="font-bold mb-6">
            Book your consultation today!
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-10 max-w-3xl mx-auto leading-relaxed font-light">
            We’re here to provide care and support whenever it’s convenient for you!
          </p>
          <Link
            to="/"
            className="solox-btn inline-block px-8 py-4 bg-[#0B2A2C] text-white rounded-full text-lg font-semibold hover:bg-[#0B2A2C] hover:text-[#0B2A2C] shadow-lg"
          >
            <span>← Back to Home</span>
          </Link>
        </div>
      </section>

      {/* Booking Section */}
      <section className="relative bg-cover bg-center py-20 px-4 sm:px-6 md:px-15 bg-[#EBE5DB] font-Joan">
        <img
          src={formbg}
          alt="Decorative"
          className="absolute top-0 left-0 w-20 h-20 sm:w-60 sm:h-40 object-contain"
        />

        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-green-950 mb-10 text-center">
            Book an Appointment
          </h2>

          <div className="flex flex-col lg:flex-row-reverse gap-8 justify-center">
            {/* Calendar + Time Picker */}
            <div className="w-full lg:w-1/2 bg-white/20 backdrop-blur-lg border border-[#0B2A2C] rounded-xl p-6 shadow-lg">
              <h3 className="font-semibold text-lg mb-6 text-green-950 text-center">
                Select your preferred Date and Time
              </h3>

              <div className="bg-white/20 backdrop-blur-lg border border-white/30 rounded-xl p-6 shadow-lg">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-6">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
                      </h4>
                      <div className="flex gap-3">
                        <button
                          onClick={() => changeMonth(-1)}
                          disabled={isPrevDisabled}
                          className={`w-9 h-9 rounded-full flex items-center justify-center text-xl transition ${isPrevDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                        >
                          ‹
                        </button>
                        <button
                          onClick={() => changeMonth(1)}
                          className="w-9 h-9 rounded-full flex items-center justify-center text-gray-600 text-xl hover:bg-gray-200 transition"
                        >
                          ›
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-7 text-xs text-gray-500 text-center mb-3 font-medium">
                      {weekDays.map((d) => <span key={d}>{d}</span>)}
                    </div>

                    <div className="grid grid-cols-7 gap-2 text-sm">
                      {Array.from({ length: startOffset }).map((_, i) => <div key={`empty-${i}`} />)}

                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const day = i + 1;
                        const isPast = isPastDate(day);
                        return (
                          <button
                            key={day}
                            disabled={isPast}
                            onClick={() => !isPast && setSelectedDate(day)}
                            className={`h-11 w-11 mx-auto rounded-full flex items-center justify-center text-sm font-medium transition
                              ${isPast ? 'text-gray-300 cursor-not-allowed' : selectedDate === day ? 'bg-[#0B2A2C] text-white' : 'text-gray-800 hover:bg-gray-200'}`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex-1 lg:max-w-[280px]">
                    <h4 className="font-semibold text-lg mb-4 text-gray-900">Pick a time</h4>
                    <div className="max-h-96 overflow-y-auto pr-2 space-y-2 scrollbar-thin scrollbar-thumb-gray-400">
                      {timeSlots.map((time) => {
                        const isDisabled = !selectedDate || isPastTimeSlot(time);

                        return (
                          <button
                            key={time}
                            onClick={() => !isDisabled && setSelectedTime(time)}
                            disabled={isDisabled}
                            className={`w-full text-left p-3 border rounded-lg transition
                              ${isDisabled 
                                ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500 line-through' 
                                : selectedTime === time 
                                  ? 'border-2 border-[#0B2A2C] bg-[#E5D8C2] text-[#0B2A2C]' 
                                  : 'border-gray-300 hover:bg-[#f0e9e0]'}`}
                          >
                            <span className="text-sm font-medium text-gray-800">{time}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Patient Form */}
            <div className="w-full lg:w-1/2">
              <div className="bg-white/20 backdrop-blur-lg border border-[#0B2A2C] rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-lg text-green-950 mb-4">Patient Details</h4>

                <div className="bg-[#E5D8C2] p-4 rounded-lg mb-6 text-sm font-medium text-green-950 text-center">
                  {selectedDate && selectedTime
                    ? `${selectedDate} ${new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' })} ${currentYear} • ${selectedTime}`
                    : 'Please select date and time first'}
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <input
                    name="patient_name"
                    placeholder="Patient Name *"
                    value={formData.patient_name}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-[#0B2A2C] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]/50"
                  />

                  <input
                    name="mobile_number"
                    placeholder="Mobile Number *"
                    value={formData.mobile_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // digits only
                      if (value.length <= 10) {
                        setFormData((prev) => ({
                          ...prev,
                          mobile_number: value,
                        }));
                      }
                    }}
                    required
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    title="Please enter a 10-digit mobile number"
                    className="w-full p-3 border border-[#0B2A2C] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]/50"
                  />

                  <input
                    name="age"
                    type="number"
                    placeholder="Age *"
                    value={formData.age}
                    onChange={handleInputChange}
                    required
                    min="1"
                    max="120"
                    className="w-full p-3 border border-[#0B2A2C] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]/50"
                  />

                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-[#0B2A2C] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]/50"
                  >
                    <option value="">Gender *</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>

                  <input
                    name="email"
                    type="email"
                    placeholder="Email (optional)"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-[#0B2A2C] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]/50"
                  />

                  <input
                    name="whatsapp_number"
                    placeholder="WhatsApp Number (optional)"
                    value={formData.whatsapp_number}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-[#0B2A2C] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]/50"
                  />

                  <textarea
                    name="problem_description"
                    placeholder="Describe the problem you are suffering from..."
                    value={formData.problem_description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-3 border border-[#0B2A2C] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]/50"
                  />

                  <label className="flex items-center mb-6 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notify_before > 0}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notify_before: e.target.checked ? 1440 : 0,
                        }))
                      }
                      className="mr-3 h-4 w-4"
                    />
                    <span className="text-sm text-green-950">Notify me 1 day before appointment</span>
                  </label>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitLoading || !selectedDate || !selectedTime}
                    className="solox-btn w-full bg-[#0B2A2C] text-white py-4 rounded-lg font-semibold hover:bg-[#0B2A2C]/90 hover:text-[#0B2A2C] disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                  >
                    {submitLoading ? (
                      <>
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                        Booking...
                      </>
                    ) : (
                      <span>Book Appointment</span>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-green-700 mb-4">Appointment Booked successfully!</h2>
            <p className="text-gray-800 mb-6">{successMessage}</p>
            <button
              onClick={closeSuccessPopup}
              className="px-8 py-3 bg-[#0B2A2C] text-white rounded-lg font-semibold hover:bg-[#0B2A2C]/90 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}